import TableList from 'components/TableList'
import { useAppSelector } from 'hooks/hooks'
import { useList } from 'hooks/listHooks'
import React from 'react'
import { Link } from 'react-router-dom'
import scheduledTerminationsSchema from 'schemes/scheduledTerminationsSchema'

interface ScheduledTerminationRow {
    scheduled_termination_id: number
    service_provider_id: number
    status: string
    businessName?: string
    explanation?: string
    [key: string]: unknown
}

interface ScheduledTerminationsListState {
    entities: {
        scheduledTerminations: Record<number, ScheduledTerminationRow>
    }
    extra: {
        definitionsScheduledTerminations: Record<string, { name: string }>
    }
    [key: string]: unknown
}

const STATUS_CLASSNAMES: Record<string, string> = {
    scheduled: 'text-[#ed7d31] font-medium',
    terminated: 'text-[#c00000]',
    cancelled: 'text-neutral-500',
    superseded: 'text-neutral-500',
}

interface ScheduledTerminationsListProps {
    filterId?: string
}

export function useScheduledTerminationsList(
    filterId: string,
    defaultOptions?: Record<string, unknown>,
): ScheduledTerminationsListState {
    const { countries } = useAppSelector((state) => state.countries)
    return useList(
        filterId,
        {
            url: 'office/scheduled-terminations/get-list',
            schema: scheduledTerminationsSchema,
            reducerKey: 'scheduledTerminations',
        },
        { ...defaultOptions, countries, sorting: ['scheduled_termination_id', 'DESC'] },
    ) as ScheduledTerminationsListState
}

const ScheduledTerminationsList = ({ filterId = 'all' }: ScheduledTerminationsListProps) => {
    const {
        entities: { scheduledTerminations = {} } = {},
        extra: { definitionsScheduledTerminations = {} } = {},
        ...list
    } = useScheduledTerminationsList(filterId)

    const renderRow = (scheduledTerminationId: number) => {
        const row = scheduledTerminations[scheduledTerminationId] || ({} as ScheduledTerminationRow)

        return {
            cols: Object.keys(definitionsScheduledTerminations).map((key) => {
                if (key === 'businessName') {
                    return (
                        <Link
                            key={key}
                            title="To pro"
                            className="plain"
                            to={`/service/providers/provider/${row.service_provider_id}`}>
                            {row.businessName}
                        </Link>
                    )
                }

                if (key === 'status') {
                    return (
                        <span key={key} className={STATUS_CLASSNAMES[row.status] ?? ''}>
                            {row.status}
                        </span>
                    )
                }

                if (key === 'explanation') {
                    return (
                        <span
                            key={key}
                            className="break-words overflow-hidden max-w-[500px] inline-block">
                            {row.explanation}
                        </span>
                    )
                }

                return <React.Fragment key={key}>{String(row[key] ?? '')}</React.Fragment>
            }),
        }
    }

    return (
        <TableList
            definitions={definitionsScheduledTerminations}
            renderRow={renderRow}
            addListFeatures
            {...list}
        />
    )
}

export default ScheduledTerminationsList
